export type PaymentSelectionKind = 'full' | 'klarna';

interface StoredPaymentSelection {
  paymentType: PaymentSelectionKind;
  serviceId?: string;
  selectedOptionKeys?: string[];
}

// Service pour gérer la sélection du mode de paiement et des options entre les pages
class PaymentSelectionService {
  private readonly STORAGE_KEY = 'payment_selection';

  private readSelection(): StoredPaymentSelection {
    try {
      const rawValue = localStorage.getItem(this.STORAGE_KEY);
      if (!rawValue) {
        return {
          paymentType: 'full',
          selectedOptionKeys: [],
        };
      }

      const parsed = JSON.parse(rawValue);

      if (typeof parsed === 'string') {
        return {
          paymentType: parsed === 'klarna' ? 'klarna' : 'full',
          selectedOptionKeys: [],
        };
      }

      if (!parsed || typeof parsed !== 'object') {
        return {
          paymentType: 'full',
          selectedOptionKeys: [],
        };
      }

      return {
        paymentType: parsed.paymentType === 'klarna' ? 'klarna' : 'full',
        serviceId: typeof parsed.serviceId === 'string' ? parsed.serviceId : undefined,
        selectedOptionKeys: Array.isArray(parsed.selectedOptionKeys)
          ? parsed.selectedOptionKeys.filter(
              (entry: unknown): entry is string => typeof entry === 'string'
            )
          : [],
      };
    } catch {
      return {
        paymentType: 'full',
        selectedOptionKeys: [],
      };
    }
  }

  private writeSelection(selection: StoredPaymentSelection): void {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(selection));
  }

  // Enregistre le choix de paiement
  public saveSelection(
    selection: PaymentSelectionKind | StoredPaymentSelection
  ): void {
    if (typeof selection === 'string') {
      const current = this.readSelection();
      this.writeSelection({
        ...current,
        paymentType: selection === 'klarna' ? 'klarna' : 'full',
      });
      return;
    }

    this.writeSelection({
      paymentType: selection.paymentType === 'klarna' ? 'klarna' : 'full',
      ...(selection.serviceId ? { serviceId: selection.serviceId } : {}),
      selectedOptionKeys: Array.isArray(selection.selectedOptionKeys)
        ? selection.selectedOptionKeys
        : [],
    });
  }

  // Récupère le choix de paiement
  public getSelection(): PaymentSelectionKind {
    return this.readSelection().paymentType;
  }

  public getCheckoutSelection(serviceId?: string): StoredPaymentSelection {
    const selection = this.readSelection();

    if (serviceId && selection.serviceId && selection.serviceId !== serviceId) {
      return {
        paymentType: selection.paymentType,
        serviceId,
        selectedOptionKeys: [],
      };
    }

    return selection;
  }

  public getSelectedOptionKeys(serviceId?: string): string[] {
    return this.getCheckoutSelection(serviceId).selectedOptionKeys || [];
  }

  // Efface le choix de paiement
  public clearSelection(): void {
    localStorage.removeItem(this.STORAGE_KEY);
  }
}

export const paymentSelectionService = new PaymentSelectionService();
